import { apiClient } from './api';
import { formatMoney, formatNumber } from '../utils/format';

export interface ServiceVariant {
  id: string;
  name: string;
  price: number;
  duration: number;
  sortOrder?: number;
}

export interface Service {
  id: string;
  name: string;
  description?: string;
  publicDescription?: string;
  /** Cena podstawowa; przy widelkach dolna granica. */
  price: number;
  /** Gorna granica widelek. Brak = cena stala. */
  priceMax?: number | null;
  duration: number;
  /** Gorna granica widelek czasu. Brak = czas staly. */
  durationMax?: number | null;
  variants?: ServiceVariant[];
  category?: string;
  salonId: string;
  isActive: boolean;
  isPublicVisible?: boolean;
  salon?: { id: string; name: string };
  staff?: { staff: { id: string; firstName: string; lastName: string } }[];
}

interface CreateServiceData {
  salonId: string;
  name: string;
  description?: string;
  publicDescription?: string;
  price: number;
  priceMax?: number | null;
  duration: number;
  durationMax?: number | null;
  category?: string;
  isPublicVisible?: boolean;
  variants?: { id?: string; name: string; price: number; duration: number }[];
}

/**
 * Ksztalt ceny rozpoznajemy po danych, tak samo jak backend — bez osobnego
 * pola "tryb", ktore moglo by sie z nimi rozjechac.
 */
export type PricingMode = 'FIXED' | 'RANGE' | 'VARIANTS';

export const pricingModeOf = (s: Pick<Service, 'priceMax' | 'variants'>): PricingMode => {
  if (s.variants && s.variants.length > 0) return 'VARIANTS';
  if (s.priceMax != null) return 'RANGE';
  return 'FIXED';
};

/** Cena do pokazania na liscie: "150 zł", "150–250 zł" albo "od 150 zł". */
export const formatServicePrice = (s: Pick<Service, 'price' | 'priceMax' | 'variants'>): string => {
  const mode = pricingModeOf(s);
  const range = (min: number, max: number) =>
    min === max ? formatMoney(min) : `${formatNumber(min, Number.isInteger(min) ? 0 : 2)}–${formatMoney(max)}`;
  if (mode === 'VARIANTS') {
    const prices = (s.variants || []).map((v) => Number(v.price));
    return range(Math.min(...prices), Math.max(...prices));
  }
  if (mode === 'RANGE') return range(Number(s.price), Number(s.priceMax));
  return formatMoney(s.price);
};

/** To samo dla czasu trwania — "90 min" albo "90–150 min". */
export const formatServiceDuration = (
  s: Pick<Service, 'duration' | 'durationMax' | 'variants'>,
): string => {
  if (s.variants && s.variants.length > 0) {
    const times = s.variants.map((v) => v.duration);
    const min = Math.min(...times);
    const max = Math.max(...times);
    return min === max ? `${min} min` : `${min}–${max} min`;
  }
  return s.durationMax != null ? `${s.duration}–${s.durationMax} min` : `${s.duration} min`;
};

export interface ServiceConsumption {
  id: string;
  quantity: number;
  unit: string;
  perClient?: number | null;
  otherParams?: {
    perMl?: number;
    clientsCount?: number;
    note?: string;
  } | null;
  product: {
    id: string;
    name: string;
    unit: string;
  };
}

export interface ServiceConsumptionInput {
  productId: string;
  quantity: number;
  perClient?: number;
  otherParams?: {
    perMl?: number;
    clientsCount?: number;
    note?: string;
  };
}

export const serviceApi = {
  async listBySalon(salonId: string, includeInactive = false) {
    const res = await apiClient.get<Service[]>(`/services/salon/${salonId}`, {
      params: includeInactive ? { includeInactive: 'true' } : {},
    });
    return res.data;
  },

  async getById(id: string) {
    const res = await apiClient.get<Service>(`/services/${id}`);
    return res.data;
  },

  async create(data: CreateServiceData) {
    const res = await apiClient.post<Service>('/services', data);
    return res.data;
  },

  async update(id: string, data: Partial<Omit<CreateServiceData, 'salonId'>>) {
    const res = await apiClient.put<Service>(`/services/${id}`, data);
    return res.data;
  },

  async delete(id: string) {
    const res = await apiClient.delete(`/services/${id}`);
    return res.data;
  },

  async assignStaff(serviceId: string, staffId: string) {
    const res = await apiClient.post(`/services/${serviceId}/staff`, { staffId });
    return res.data;
  },

  async unassignStaff(serviceId: string, staffId: string) {
    const res = await apiClient.delete(`/services/${serviceId}/staff/${staffId}`);
    return res.data;
  },

  async getConsumptions(serviceId: string) {
    const res = await apiClient.get<{ data: ServiceConsumption[] }>(`/services/${serviceId}/consumptions`);
    return res.data.data;
  },

  async setConsumptions(
    serviceId: string,
    items: ServiceConsumptionInput[],
  ) {
    const res = await apiClient.put<{ data: ServiceConsumption[] }>(`/services/${serviceId}/consumptions`, { items });
    return res.data.data;
  },
};
